import React, { useState } from "react";
import {
  Box, Card, CardContent, Typography, Chip,
  TextField, MenuItem, Button, Pagination,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, InputAdornment, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Avatar,
} from "@mui/material";
import { Search, Download, Eye, PhoneCall, Inbox, X, Mail, Phone, Globe, Tag } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import AdminShell from "../layout/AdminShell";
import { adminGetLeads, adminExportLeads } from "../../api/leads";
import toast from "react-hot-toast";

const STATUS_COLORS = {
  NEW: "default", CONTACTED: "primary", FOLLOW_UP: "warning",
  QUALIFIED: "info", CONVERTED: "success", LOST: "error",
};

const PRIORITY_COLORS = { HOT: "error", WARM: "warning", COLD: "info" };

// Static, forward-looking list — matches the dropdown on the public contact
// form. Older leads in the DB may have inconsistent values (e.g. "Course",
// "course_enquiry") from before this list was standardized, so the filter
// may not catch every legacy record.
const ENQUIRY_TYPES = [
  { value: "course", label: "Course Enquiry" },
  { value: "software", label: "Software Development" },
  { value: "hardware", label: "Hardware & Infrastructure" },
  { value: "placement", label: "Placement / Hiring" },
  { value: "corporate_training", label: "Corporate Training" },
  { value: "managed_services", label: "Managed IT Services" },
  { value: "other", label: "Other" },
];

function DetailRow({ icon: Icon, label, value, link }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 0.9 }}>
      <Icon size={15} color="#94A3B8" style={{ marginTop: 2, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.3 }}>{label}</Typography>
        {link ? (
          <Typography
            component="a" href={link} target="_blank" rel="noreferrer"
            sx={{ fontSize: 13.5, color: "#2563EB", fontWeight: 600, wordBreak: "break-word", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            {value}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A", wordBreak: "break-word" }}>{value}</Typography>
        )}
      </Box>
    </Box>
  );
}

function LeadDetailsModal({ lead, onClose, onFollowUp, onOpenFull }) {
  if (!lead) return null;
  const initials = (lead.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Dialog open={!!lead} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: "rgba(37,99,235,0.1)", color: "#2563EB", fontWeight: 700, fontSize: 15 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>{lead.name}</Typography>
            <Box sx={{ display: "flex", gap: 0.6, mt: 0.4, flexWrap: "wrap" }}>
              <Chip label={lead.status_display || lead.status} color={STATUS_COLORS[lead.status] || "default"} size="small" />
              {lead.priority && <Chip label={lead.priority_display || lead.priority} color={PRIORITY_COLORS[lead.priority] || "default"} size="small" />}
            </Box>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", mb: 0.5, mt: 0.5 }}>Contact</Typography>
        <DetailRow icon={Mail} label="Email" value={lead.email} link={`mailto:${lead.email}`} />
        <DetailRow icon={Phone} label="Phone" value={lead.phone} link={`tel:${lead.phone}`} />

        <Divider sx={{ my: 1.5 }} />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", mb: 0.5 }}>Enquiry</Typography>
        <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mb: 1 }}>
          <Chip label={`Visitor: ${lead.visitor_type_display || lead.visitor_type}`} size="small" variant="outlined" />
          {lead.enquiry_type && <Chip label={`Enquiry: ${lead.enquiry_type}`} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />}
        </Box>
        <Typography sx={{ fontSize: 13.5, color: "#334155", lineHeight: 1.6, mt: 0.75 }}>
          {lead.requirement_text || "—"}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", mb: 0.5 }}>Source</Typography>
        <DetailRow icon={Tag} label="Source" value={lead.source} />
        <DetailRow icon={Globe} label="Source URL" value={lead.source_url} link={lead.source_url || undefined} />
        {(lead.utm_source || lead.utm_medium || lead.utm_campaign) && (
          <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 1 }}>
            {lead.utm_source && <Chip label={`utm_source: ${lead.utm_source}`} size="small" variant="outlined" />}
            {lead.utm_medium && <Chip label={`utm_medium: ${lead.utm_medium}`} size="small" variant="outlined" />}
            {lead.utm_campaign && <Chip label={`utm_campaign: ${lead.utm_campaign}`} size="small" variant="outlined" />}
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", mb: 0.5 }}>Assignment & Notes</Typography>
        <Typography sx={{ fontSize: 13.5, color: "#0F172A", fontWeight: 600, mb: 0.5 }}>
          {lead.assigned_to_name || "Unassigned"}
        </Typography>
        {lead.notes && (
          <Typography sx={{ fontSize: 13.5, color: "#334155", lineHeight: 1.6, mt: 0.5 }}>{lead.notes}</Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
          Created {dayjs(lead.created_at).format("DD MMM YYYY, h:mm A")}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
          Updated {dayjs(lead.updated_at).format("DD MMM YYYY, h:mm A")}
        </Typography>
      </DialogContent>

    
    </Dialog>
  );
}

export default function LeadsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "", visitor_type: "", enquiry_type: "", search: "" });
  const [viewLead, setViewLead] = useState(null);

  const params = { page, page_size: 20, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };

  const { data, isLoading } = useQuery({
    queryKey: ["admin-leads", params],
    queryFn: () => adminGetLeads(params).then(r => r.data),
  });

  const handleExport = async () => {
    try {
      const { data: res } = await adminExportLeads(
        Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      );
      const blob = new Blob([res.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${dayjs().format("YYYY-MM-DD")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleFollowUp = (id) => {
    navigate(`/admin/leads/${id}?action=follow-up`);
  };

  const handleOpenFull = (id) => {
    navigate(`/admin/leads/${id}`);
  };

  return (
    <AdminShell title="Leads">
      {/* Filters */}
      <Card sx={{ mb: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              placeholder="Search name, email, phone"
              size="small"
              sx={{ minWidth: 240 }}
              value={filters.search}
              onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} color="#94A3B8" /></InputAdornment> }}
            />
            <TextField
              select size="small" label="Status" sx={{ minWidth: 150 }}
              value={filters.status}
              onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {["NEW","CONTACTED","FOLLOW_UP","QUALIFIED","CONVERTED","LOST"].map(s =>
                <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>
              )}
            </TextField>
            <TextField
              select size="small" label="Visitor Type" sx={{ minWidth: 150 }}
              value={filters.visitor_type}
              onChange={(e) => { setFilters(f => ({ ...f, visitor_type: e.target.value })); setPage(1); }}
            >
              <MenuItem value="">All visitors</MenuItem>
              {["STUDENT","PROFESSIONAL","COMPANY","OTHER"].map(v =>
                <MenuItem key={v} value={v}>{v.charAt(0) + v.slice(1).toLowerCase()}</MenuItem>
              )}
            </TextField>
            <TextField
              select size="small" label="Enquiry Type" sx={{ minWidth: 170 }}
              value={filters.enquiry_type}
              onChange={(e) => { setFilters(f => ({ ...f, enquiry_type: e.target.value })); setPage(1); }}
            >
              <MenuItem value="">All enquiries</MenuItem>
              {ENQUIRY_TYPES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined" size="small"
              startIcon={<Download size={15} />}
              onClick={handleExport}
              sx={{ ml: "auto", textTransform: "none", fontWeight: 600, borderRadius: "10px", borderColor: "rgba(15,23,42,0.15)", color: "#0F172A" }}
            >
              Export CSV
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Name", "Contact", "Enquiry Type", "Visitor", "Status", "Priority", "Assigned To", "Date", ""].map(h =>
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{h}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : data?.results?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 7, color: "text.secondary" }}>
                    <Inbox size={28} style={{ opacity: 0.35, marginBottom: 8 }} />
                    <Typography fontSize={14}>No leads found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data?.results?.map((lead) => (
                  <TableRow key={lead.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13.5 }}>{lead.name}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13 }}>{lead.email}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{lead.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={lead.enquiry_type || "—"} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={lead.visitor_type_display || lead.visitor_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={lead.status_display || lead.status} color={STATUS_COLORS[lead.status] || "default"} size="small" />
                    </TableCell>
                    <TableCell>
                      {lead.priority && (
                        <Chip label={lead.priority_display || lead.priority} color={PRIORITY_COLORS[lead.priority] || "default"} size="small" />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {lead.assigned_to_name || <Typography component="span" sx={{ fontSize: 13, color: "text.secondary" }}>Unassigned</Typography>}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 12.5, whiteSpace: "nowrap" }}>
                      {dayjs(lead.created_at).format("DD MMM YYYY")}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setViewLead(lead)}>
                          <Eye size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add follow-up">
                        <IconButton size="small" onClick={() => handleFollowUp(lead.id)}>
                          <PhoneCall size={15} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
        {data?.total > 20 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Pagination
              count={Math.ceil(data.total / 20)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      <LeadDetailsModal
        lead={viewLead}
        onClose={() => setViewLead(null)}
        onFollowUp={(id) => { setViewLead(null); handleFollowUp(id); }}
        onOpenFull={(id) => { setViewLead(null); handleOpenFull(id); }}
      />
    </AdminShell>
  );
}